import { Box, Grid, useMediaQuery } from '@mui/material';
import { useState, useMemo, useContext } from 'react';
import {
  Card,
  Table,
  MobileTable,
  Pagination,
  CSVDownloadButton,
  Loader
} from '../../../components';
import { SALES_PORTAL_PER_PAGE_OPTIONS, formatMoney, getEnvironment } from '../../../helpers';
import { ISortDirection, IDateRange, ISalesAgentReport, IResponse } from '../../../models';
import { SalesContext, update } from '../../../context/sales';
import { getSalesAgentReport } from '../../../fetch';
import { SellersAgentFilters } from './sellers-agent-filters';
import { useQuery, useQueryClient } from 'react-query';

const PAGE_NAME = 'sellers-agent-report';
export const SellersAgent = () => {
  const queryClient = useQueryClient();
   const isMobile = useMediaQuery('(max-width: 960px)');

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    dateFrom?: string;
    dateTo?: string;
    neighborhoodId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedSort, setSelectedSort] = useState<string>('Neighborhood');
  const [sortDirection, setSortDirection] = useState<{
    Neighborhood?: ISortDirection;
    Lot?: ISortDirection;
    FinalSalePrice?: ISortDirection;
    AgentName?: ISortDirection;
    AgentCompany?: ISortDirection;
    AgentPhone?: ISortDirection;
    AgentEmail?: ISortDirection;
  }>({
    Neighborhood: 'Asc'
  });

  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });

  const { isLoading, data: reportData } = useQuery<IResponse<ISalesAgentReport[]>, Error>(
    ['getSalesAgentReport', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getSalesAgentReport({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        dateFrom: selectedDateRange.startDate?.toISOString(),
        dateTo: selectedDateRange.endDate?.toISOString(),
        territoryId: selectedTerritoryId,
        neighborhoodId: selectedNeighborhoodId as string
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked',
      enabled: !!selectedDateRange?.startDate && !!selectedDateRange?.endDate
    }
  );

  const data = useMemo(() => reportData?.records ?? [], [reportData]);

  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      {
        Header: 'Lot',
        accessor: 'lot',
        isServerSorted: selectedSort === 'Lot',
        isServerSortedDesc: sortDirection.Lot === 'Desc',
        handleClickColumn: () => handleClickColumn('Lot')
      },
      {
        Header: 'Price',
        accessor: 'finalSalePrice',
        sort: false,
        Cell: props => <>{formatMoney(props.cell.row.original.finalSalePrice)}</>
      },
      {
        Header: 'Agent Name',
        accessor: 'agentName',
        isServerSorted: selectedSort === 'AgentName',
        isServerSortedDesc: sortDirection.AgentName === 'Desc',
        handleClickColumn: () => handleClickColumn('AgentName')
      },
      {
        Header: 'Agent Company',
        accessor: 'agentCompany',
        isServerSorted: selectedSort === 'AgentCompany',
        isServerSortedDesc: sortDirection.AgentCompany === 'Desc',
        handleClickColumn: () => handleClickColumn('AgentCompany')
      },
      {
        Header: 'Agent Phone',
        accessor: 'agentPhone',
        isServerSorted: selectedSort === 'AgentPhone',
        isServerSortedDesc: sortDirection.AgentPhone === 'Desc',
        handleClickColumn: () => handleClickColumn('AgentPhone')
      },
      {
        Header: 'Agent Email',
        accessor: 'agentEmail',
        isServerSorted: selectedSort === 'AgentEmail',
        isServerSortedDesc: sortDirection.AgentEmail === 'Desc',
        handleClickColumn: () => handleClickColumn('AgentEmail')
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);
  return (
    <>
      <Box mt={1}>
        <Card title="Options">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SellersAgentFilters
                isLoading={isLoading}
                setHasAppliedFilters={setHasAppliedFilters}
                hasAppliedFilters={hasAppliedFilters}
                setSelectedDateRange={setSelectedDateRange}
                selectedDateRange={selectedDateRange}
                setSelectedTerritoryId={setSelectedTerritoryId}
                selectedTerritoryId={selectedTerritoryId}
                setSelectedNeighborhoodId={setSelectedNeighborhoodId}
                selectedNeighborhoodId={selectedNeighborhoodId}
                applyFilters={(clearFilters?: boolean) => {
                  if (clearFilters) {
                    setSelectedDateRange({ startDate: null, endDate: null, key: 'selection', inputValue: '' });
                    setSelectedTerritoryId('');
                    setSelectedNeighborhoodId('');
                    setPage(0);
                    setRecordCount(0);
                    queryClient.removeQueries(['getSalesAgentReport']);
                  } else {
                    setPage(0);
                    setFilters({
                      ...filters,
                      dateFrom: selectedDateRange.startDate?.toISOString(),
                      dateTo: selectedDateRange.endDate?.toISOString(),
                      territoryId: selectedTerritoryId,
                      neighborhoodId: selectedNeighborhoodId,
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>
      {isLoading && !data && (
        <Box mt={1}>
          <Loader position="centered" />
        </Box>
      )}
      {data && (
        <>
          <Box
            display="flex"
            justifyContent={isMobile ? 'flex-start' : 'flex-end'}
            mb={isMobile ? 1 : null}
          >
            <CSVDownloadButton
              name="sellers-agent-csv"
              fileNamePrefix="sellers_agent_report"
              disabled={data?.length === 0}
              columns={[
                { header: 'Neighborhood', key: 'neighborhood' },
                { header: 'Lot', key: 'lot' },
                { header: 'Final Sale Price', key: 'finalSalePrice' },
                { header: 'Agent Name', key: 'agentName' },
                { header: 'Agent Company', key: 'agentCompany' },
                { header: 'Agent Phone', key: 'agentPhone' },
                { header: 'Agent Email', key: 'agentEmail' }
              ]}
              apiRequest={() =>
                getSalesAgentReport({
                  dateFrom: selectedDateRange.startDate?.toISOString(),
                  dateTo: selectedDateRange.endDate?.toISOString(),
                  territoryId: selectedTerritoryId,
                  neighborhoodId: selectedNeighborhoodId,
                  perPage: null,
                  sortBy: selectedSort,
                  sortDirection: sortDirection[selectedSort] as string
                })
              }
              transformResponse={(res: IResponse<ISalesAgentReport[]>) => res?.records}
            >
              CSV
            </CSVDownloadButton>
          </Box>
          <Table
            stickyHeaders
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            hidePagination
            mobileProps={{
              fields: columns
                .filter(c => c.Header)
                .map(c => ({
                  name: c.Header,
                  accessor: c.accessor,
                  Cell: c?.Cell
                }))
            }}
            ResponsiveComponent={isMobile ? MobileTable : undefined}
          />
          {!isLoading && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
              setRowsPerPage={itemsPerPage => {
                return dispatchPerPage(
                  update({
                    pageName: PAGE_NAME,
                    environment: getEnvironment(),
                    itemsPerPage: itemsPerPage
                  })
                );
              }}
            />
          )}      
        </>
      )}
    </>
  );
};
